(ns arthur.domain.clipboard
  "Pure multi-node clipboard commands.

  A clipboard value is a detached forest of node maps. Normal copies keep symbol
  references; `duplicate` with `:unique?` copies the complete referenced symbol
  graph once for the whole forest. UI state, playhead conversion, and history
  stay in events.ui. See docs/clipboard-plan.md."
  (:require [arthur.domain.bring :as bring]
            [arthur.domain.clip :as clip]
            [arthur.domain.nest :as nest]
            [arthur.domain.node :as node]
            [arthur.domain.span :as span]
            [arthur.domain.symbol :as symbol]))

(defn- prefix? [a b]
  (and (<= (count a) (count b)) (= a (subvec b 0 (count a)))))

(defn- node-selections [clip selections]
  (->> selections
       (keep (fn [[kind sid id path :as address]]
               (when (and (= :node kind) id (get-in clip [:symbols sid :nodes id]))
                 {:address address :sid sid :id id :path (vec (or path [id]))})))
       ;; One owned node reached through two shared occurrences is still one edit.
       (reduce (fn [{:keys [seen out] :as acc} {:keys [sid id] :as x}]
                 (if (contains? seen [sid id]) acc
                   {:seen (conj seen [sid id]) :out (conj out x)}))
               {:seen #{} :out []})
       :out))

(defn canonical
  "Valid selected node occurrences, with anything visibly below another selected
  occurrence omitted. Order is selection order and therefore keeps the primary
  member last in the ordinary case."
  [clip selections]
  (let [xs (node-selections clip selections)]
    (filterv (fn [{p :path}]
               (not-any? (fn [{q :path}]
                           (and (< (count q) (count p)) (prefix? q p)))
                         xs))
             xs)))

(defn- subtree [nodes root]
  (into {} (filter (fn [[id _]] (some #{root} (symbol/lineage nodes id)))) nodes))

(defn snapshot
  "Snapshot the canonical selected forest, or `{:refused why}`."
  [clip selections]
  (let [roots (canonical clip selections)]
    (if (empty? roots)
      {:refused "select something to copy"}
      {:clipboard
       {:items
        (mapv (fn [{:keys [sid id path]}]
                (let [nodes (get-in clip [:symbols sid :nodes])]
                  {:sid sid :root id :path path :parent (:parent (get nodes id))
                   :nodes (subtree nodes id)}))
              roots)}})))

(defn cut
  "Delete a previously snapshotted forest. Snapshotting first is what makes cut
  retain data even though its source nodes are gone."
  [clip {:keys [items]}]
  (let [after (reduce (fn [c {:keys [sid root]}] (nest/delete-node c sid root)) clip items)]
    (if-let [why (first (clip/problems after))]
      {:refused why}
      {:clip after :selections []})))

(defn- fresh
  [taken fresh-id]
  (loop [id (fresh-id)]
    (if (contains? taken id) (recur (fresh-id)) id)))

(defn- allocate
  [clip items fresh-id]
  (loop [pending (vec (mapcat (fn [[i item]] (map #(vector i %) (keys (:nodes item))))
                               (map-indexed vector items)))
         taken (into #{} (mapcat (comp keys :nodes val) (:symbols clip)))
         ids {}]
    (if-let [k (first pending)]
      (let [id (fresh taken fresh-id)]
        (recur (subvec pending 1) (conj taken id) (assoc ids k id)))
      ids)))

(defn- unique-content
  [clip items unique?]
  (let [roots (into #{} (comp (mapcat #(vals (:nodes %))) (keep node/source)) items)]
    (if (and unique? (seq roots))
      (let [{c :clip ids :ids} (bring/symbols clip clip roots {})]
        [c ids])
      [clip {}])))

(defn- materialize
  [clip items fresh-id unique? paste?]
  (let [[clip source-ids] (unique-content clip items unique?)
        ids (allocate clip items fresh-id)
        made
        (mapv
         (fn [[i {:keys [sid root path parent nodes]}]]
           (let [id-of #(get ids [i %])
                 copied (into {}
                              (map (fn [[old n]]
                                     (let [id (id-of old)]
                                       [id (cond-> (assoc n :id id)
                                             (:parent n) (assoc :parent (id-of (:parent n)))
                                             (:stencil n) (assoc :stencil (id-of (:stencil n)))
                                             (node/source n)
                                             (assoc-in [:source :symbol]
                                                       (get source-ids (node/source n)
                                                            (node/source n))))])))
                              nodes)
                 new-root (id-of root)
                 ;; Paste reparents roots into one explicit destination.
                 ;; Duplicate leaves them beside their originals.
                 copied (assoc-in copied [new-root :parent]
                                  (when-not paste? parent))]
             {:source-sid sid :old-root root :path path :root new-root
              :nodes copied}))
         (map-indexed vector items))]
    {:clip clip :items made}))

(defn- shifted [n delta]
  (if (node/placed-span n)
    (update-in n [:time :at] (fnil + 0) delta)
    n))

(defn- top-zs [nodes n]
  (let [base (or (last (sort (map #(or (:z %) "") (vals nodes)))) "")]
    (map #(str base (apply str (repeat % "m"))) (range 1 (inc n)))))

(defn- add-composition
  [clip sid items at]
  (let [starts (keep #(some-> (get-in % [:nodes (:root %)]) node/placed-span first) items)
        anchor (when (seq starts) (apply min starts))
        delta (if anchor (- at anchor) 0)
        existing (get-in clip [:symbols sid :nodes])
        zs (top-zs existing (count items))
        nodes (reduce (fn [nodes [{:keys [root] copied :nodes} z]]
                        (into nodes (assoc-in copied [root]
                                             (-> (get copied root)
                                                 (shifted delta)
                                                 (assoc :z z)))))
                      existing (map vector items zs))]
    (assoc-in clip [:symbols sid :nodes] nodes)))

(defn- add-lane
  [clip sid items at fresh-id]
  (let [roots (map #(get-in % [:nodes (:root %)]) items)
        starts (map #(some-> % node/placed-span first) roots)
        anchor (when (every? some? starts) (apply min starts))
        intervals (when anchor
                    (sort-by first
                             (map (fn [n]
                                    (let [[lo hi] (node/placed-span n)]
                                      [(+ at (- lo anchor)) (+ at (- hi anchor))]))
                                  roots)))
        overlaps? (some (fn [[[a b] [c d]]] (and (< a d) (< c b)))
                        (partition 2 1 intervals))]
    (if (some nil? starts)
      {:refused "a lane accepts copied things only when they have a finite span"}
      (if overlaps?
        {:refused "overlapping copied things cannot be pasted into one lane"}
        (reduce
         (fn [result item]
           (if (:refused result)
             (reduced result)
             (let [c (:clip result)
                   root (:root item)
                   n (get-in item [:nodes root])
                   desired (+ at (- (first (node/placed-span n)) anchor))
                   r (span/place-node c sid n desired
                                      {:extent :grow-symbol
                                       :remainder-id (fresh (into #{} (keys (get-in c [:symbols sid :nodes])))
                                                            fresh-id)})]
               (if-let [made (:clip r)]
                 {:clip (update-in made [:symbols sid :nodes]
                                   into (dissoc (:nodes item) root))}
                 r))))
         {:clip clip} items)))))

(defn paste
  "Paste `clipboard` into `sid`, anchoring its first finite start at `at`.
  `fresh-id` is supplied by the event so this domain command remains testable."
  [clip clipboard sid at {:keys [fresh-id] :or {fresh-id random-uuid}}]
  (cond
    (nil? (clip/symbol clip sid)) {:refused "the paste target no longer exists"}
    (not (and (integer? at) (not (neg? at))))
    {:refused "the playhead is not on one frame of the paste target"}
    (empty? (:items clipboard)) {:refused "there is nothing to paste"}
    :else
    (let [{base :clip items :items} (materialize clip (:items clipboard) fresh-id false true)
          r (if (symbol/lane? (clip/symbol base sid))
              (add-lane base sid items at fresh-id)
              {:clip (add-composition base sid items at)})
          made (:clip r)
          why (when made (first (clip/problems made)))]
      (cond
        (:refused r) r
        why {:refused why}
        :else {:clip made :roots (mapv :root items)}))))

(defn- add-duplicate-composition [clip sid items]
  (let [existing (get-in clip [:symbols sid :nodes])
        zs (top-zs existing (count items))]
    (assoc-in clip [:symbols sid :nodes]
              (reduce (fn [nodes [{:keys [root] copied :nodes} z]]
                        (into nodes (assoc-in copied [root :z] z)))
                      existing (map vector items zs)))))

(defn- add-duplicate-lane
  [clip sid items fresh-id]
  (let [old-roots (map #(get-in clip [:symbols sid :nodes (:old-root %)]) items)
        spans (map node/placed-span old-roots)
        start (apply min (map first spans))
        end (apply max (map second spans))
        duration (- end start)
        selected (set (map :old-root items))
        shifted-clip
        (update-in clip [:symbols sid :nodes]
                   (fn [nodes]
                     (reduce (fn [ns n]
                               (let [lo (some-> (node/placed-span n) first)]
                                 (if (and (nil? (:parent n))
                                          (not (contains? selected (:id n)))
                                          lo (>= lo end))
                                 (update-in ns [(:id n) :time :at] (fnil + 0) duration)
                                 ns)))
                             nodes (vals nodes))))]
    (reduce
     (fn [result item]
       (if (:refused result)
         (reduced result)
         (let [c (:clip result)
               root (:root item)
               n (get-in item [:nodes root])
               old (get-in clip [:symbols sid :nodes (:old-root item)])
               desired (+ (first (node/placed-span old)) duration)
               r (span/place-node c sid n desired
                                  {:extent :grow-symbol
                                   :remainder-id (fresh (into #{} (keys (get-in c [:symbols sid :nodes])))
                                                        fresh-id)})]
           (if-let [made (:clip r)]
             {:clip (update-in made [:symbols sid :nodes]
                               into (dissoc (:nodes item) root))}
             r))))
     {:clip shifted-clip}
     (sort-by #(first (node/placed-span
                       (get-in clip [:symbols sid :nodes (:old-root %)]))) items))))

(defn duplicate
  "Duplicate a snapshot beside its sources. Direct finite children of lane
  symbols repeat forward and ripple later cels; everything else copies in place.
  With `:unique?`, referenced symbol graphs are deep-copied once for the batch."
  [clip clipboard {:keys [fresh-id unique?] :or {fresh-id random-uuid}}]
  (if (empty? (:items clipboard))
    {:refused "select something to duplicate"}
    (let [{base :clip items :items}
          (materialize clip (:items clipboard) fresh-id unique? false)
          groups (vals (group-by :source-sid items))
          result
          (reduce
           (fn [result group]
             (if (:refused result)
               (reduced result)
               (let [c (:clip result)
                     sid (:source-sid (first group))
                     lane? (symbol/lane? (clip/symbol c sid))
                     [lane-items other]
                     ((juxt filter remove)
                      #(let [old (get-in clip [:symbols sid :nodes (:old-root %)])]
                         (and lane? (nil? (:parent old)) (node/placed-span old)))
                      group)
                     c (if (seq other) (add-duplicate-composition c sid other) c)
                     r (if (seq lane-items)
                         (add-duplicate-lane c sid lane-items fresh-id)
                         {:clip c})]
                 r)))
           {:clip base} groups)
          made (:clip result)
          why (when made (first (clip/problems made)))]
      (cond
        (:refused result) result
        why {:refused why}
        :else {:clip made
               :roots (mapv (fn [{:keys [source-sid root path]}]
                              {:sid source-sid :id root
                               :path (conj (vec (butlast path)) root)})
                            items)}))))

(defn move-many
  "Reparent the selected roots atomically, preserving their world transforms and
  clocks. Optional delta places the forest later on the open ruler."
  [document store open selections to frame delta]
  (let [roots (canonical document selections)
        under? (fn [path] (prefix? path (vec to)))]
    (cond
      (empty? roots) {:refused "select something to move"}
      (some #(under? (:path %)) roots) {:refused "a selection cannot go inside itself"}
      :else
      (let [moved
            (reduce (fn [result {:keys [path]}]
                      (if (:refused result) (reduced result)
                        (let [r (nest/move-node (:clip result) store open path to frame)]
                          (if (:refused r) (reduced r)
                            {:clip (:clip r)
                             :selections (conj (:selections result)
                                               [:node (:sid r) (:id r) (conj (vec to) (:id r))])}))))
                    {:clip document :selections []} roots)]
        (if (:refused moved) moved
          (let [shifted (nest/slide-many (:clip moved) open
                                         (mapv #(nth % 3) (:selections moved)) (or delta 0))
                checked (if (:refused shifted) shifted
                          (reduce (fn [result sid]
                                    (if (:refused result) (reduced result)
                                      (span/finish (:clip result) sid
                                                   (get-in (:clip result) [:symbols sid :nodes])
                                                   nil :grow-symbol)))
                                  shifted (distinct (map :sid roots))))]
            (if (:refused checked) checked
              (if-let [why (first (clip/problems (:clip checked)))]
                {:refused why}
                (assoc checked :selections (:selections moved))))))))))
